import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ActionList, Autocomplete, Badge, Banner, Button, Card, Checkbox, IndexTable, Modal, Page, Pagination, Popover, ProgressBar, Select, Spinner, Text, TextField, Tooltip } from '@shopify/polaris';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import { DeleteIcon } from '@shopify/polaris-icons';
import ProductThumbnail from '../../app/components/ProductThumbnail';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { DetectedProductFitment, ProductDetails, ProductFitmentCopyJob, ProductFitmentCopySource, ProductFitmentPage, RealtimeProduct, SaveFitmentChangesPayload, VariantFitmentReview } from '../../app/models/Product';
import { useStore } from '../../app/stores/store';
import { getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';
import { productStatusLabel } from '../../app/utils/productStatus';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldValues,
    getLegacyFieldStateKey,
    getSelectedFitmentLabel,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    hasRequiredFitmentFieldValues,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';

import { FitmentDraft, fitmentKey, FITMENT_CHANGE_LIMIT } from './FitmentDraft';
import ProductSummaryCard from './ProductSummaryCard';
import { FitmentOptionsCache, useFitmentOptions } from './useFitmentOptions';
import { CopyHistoryModal, RecentCopyActivity } from './ProductCopyActivity';
import { VariantReviewDialog, VariantTargetPicker, variantMissingMatches } from './VariantFitmentDialogs';
import './ProductDetailsPage.css';

type SelectOption = { label: string; value: string };
type AssignedFitment = ProductDetails['fitments'][number];
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];
type AssignedFitmentView = 'grouped' | 'individual';
type AssignedFitmentRow = {
    key: string;
    fitments: AssignedFitment[];
    fieldValues: Record<string, string>;
};
type YearRangeDraft = {
    id: string;
    start: string;
    end: string;
    autoFillEnd?: boolean;
};
type FitmentEditDraft = {
    rowKey: string;
    fitmentKeys: string[];
    fieldValues: Record<string, string>;
    ranges: YearRangeDraft[];
    noteLines: string[];
    focusNotes: boolean;
};
type CopySourceSelection = {
    product: RealtimeProduct;
    source: ProductFitmentCopySource;
};

const productScopeValue = 'product';
const variantScopePrefix = 'variant:';
const assignedFitmentPageSize = 100;

const getVariantScopeValue = (variantId: number) => `${variantScopePrefix}${variantId}`;

const formatFitmentCountLabel = (count: number) => `${count} ${count === 1 ? 'fitment' : 'fitments'}`;

const getFitmentIdentity = (fitment: AssignedFitment) =>
    fitment.version ? 'assignment:' + fitment.id : 'draft:' + fitmentKey(fitment);

const getVariantIdFromScope = (scope: string): number | null => {
    if (!scope.startsWith(variantScopePrefix)) {
        return null;
    }

    const variantId = Number(scope.slice(variantScopePrefix.length));
    return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
};

const sortDetectedFitments = (a: DetectedProductFitment, b: DetectedProductFitment) => {
    const yearCompare = Number(b.year) - Number(a.year);
    if (yearCompare !== 0) {
        return yearCompare;
    }

    const makeCompare = a.make.localeCompare(b.make);
    if (makeCompare !== 0) {
        return makeCompare;
    }

    const modelCompare = a.model.localeCompare(b.model);
    if (modelCompare !== 0) {
        return modelCompare;
    }

    return (a.submodel || '').localeCompare(b.submodel || '');
};

const escapeVehicleTagSegment = (value: string) =>
    value.trim().replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const normalizeFitmentNoteLines = (value: string | string[], preserveBlankLines = false) => {
    const lines = (Array.isArray(value) ? value : value.split(/\r?\n/))
        .map((note) => note.trim());

    return preserveBlankLines ? lines : lines.filter(Boolean);
};

const getFitmentNoteLines = (fitment: ProductDetails['fitments'][number], includeBlankLines = false) =>
    (fitment.notes ?? [])
        .map((note) => note.text ?? '')
        .filter((note) => includeBlankLines || Boolean(note));

const getFitmentNotesText = (fitment: ProductDetails['fitments'][number]) =>
    getFitmentNoteLines(fitment).join('\n');

// The UI edits each note as its own row. Once edited from the PDP, the saved
// notes become Manual.
const buildManualFitmentNotes = (value: string | string[], preserveBlankLines = false) =>
    normalizeFitmentNoteLines(value, preserveBlankLines).map((text, index) => ({
        // Negative IDs only stabilize unsaved React rows; the API persists new IDs.
        id: -(index + 1),
        text,
        source: 'Manual',
        qualifierId: null,
        sortOrder: index + 1,
    }));

type FitmentNotesEditorProps = {
    label: string;
    noteLines: string[];
    disabled?: boolean;
    onChange: (noteLines: string[]) => void;
    autoFocus?: boolean;
};

const FitmentNotesEditor = ({
    label,
    noteLines,
    disabled = false,
    onChange,
    autoFocus = false,
}: FitmentNotesEditorProps) => {
    const rows = noteLines;

    const updateLine = (index: number, value: string) => {
        const nextLines = [...rows];
        nextLines[index] = value;
        onChange(nextLines);
    };

    const removeLine = (index: number) => {
        onChange(rows.filter((_, rowIndex) => rowIndex !== index));
    };

    return (
        <div className="pf-fitment-notes-editor">
            <div className="pf-fitments-section-heading">
                <div className="pf-fitment-notes-heading">
                    <Text as="h4" variant="headingSm">{label}</Text>
                    <Text as="span" variant="bodySm" tone="subdued">Optional</Text>
                </div>
                <Button variant="plain" onClick={() => onChange([...rows, ''])} disabled={disabled}>+ Add note</Button>
            </div>
            {rows.length === 0 && <Text as="p" tone="subdued">No notes added.</Text>}
            {rows.map((value, index) => (
                <div key={`fitment-note-${index}`} className="pf-fitment-note-row">
                    <TextField
                        label={`${label} ${index + 1}`}
                        labelHidden={rows.length === 1}
                        value={value}
                        onChange={(nextValue) => updateLine(index, nextValue)}
                        autoComplete="off"
                        disabled={disabled}
                        placeholder={`Note ${index + 1}`}
                        multiline={2}
                        autoFocus={autoFocus && index === 0}
                    />
                    <Button
                        variant="plain"
                        tone="critical"
                        onClick={() => removeLine(index)}
                        disabled={disabled}
                        accessibilityLabel={`Remove note ${index + 1}`}
                    >
                        Remove
                    </Button>
                </div>
            ))}
        </div>
    );
};

const buildStableClientTagId = (tag: string) =>
    Math.abs(tag.split('').reduce((hash, character) => ((hash * 31) + character.charCodeAt(0)) | 0, 17));

const buildFitmentTagFromValues = (values: FitmentFieldValues, useYmmPrefix: boolean) => {
    const legacyValues = getLegacyFieldValues(values);
    const legacyTagSegments = [legacyValues.make, legacyValues.model, legacyValues.submodel, legacyValues.year]
        .filter(Boolean)
        .map(escapeVehicleTagSegment);
    const baseTag = legacyTagSegments.join('-');

    return baseTag ? (useYmmPrefix ? `ymm_${baseTag}` : baseTag) : '';
};

const buildDraftFitmentFromValues = (
    values: FitmentFieldValues,
    fields: FitmentField[],
    useYmmPrefix: boolean,
    notes: AssignedFitment['notes'] = [],
    sourceType = 'Manual',
    preserveOtherFields = false,
): AssignedFitment => {
    const normalizedValues = preserveOtherFields
        ? Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim()).map(([key, value]) => [key, value.trim()]))
        : normalizeFitmentFieldValues(fields, values);
    const legacyValues = getLegacyFieldValues(normalizedValues);
    const fitmentKey = JSON.stringify(normalizedValues);
    const tag = buildFitmentTagFromValues(normalizedValues, useYmmPrefix);

    return {
        id: buildStableClientTagId(tag || fitmentKey),
        tag,
        year: legacyValues.year,
        make: legacyValues.make,
        model: legacyValues.model,
        submodel: legacyValues.submodel,
        fieldValues: normalizedValues,
        sourceType,
        source: tag || getSelectedFitmentLabel(fields, normalizedValues),
        canRemove: true,
        createdAt: new Date().toISOString(),
        notes: (notes ?? []).map((note) => ({ ...note })),
    };
};

const getProductFitmentFieldValue = (
    fitment: ProductDetails['fitments'][number] | DetectedProductFitment,
    key: ReturnType<typeof getLegacyFieldStateKey>,
) => {
    if (key === 'year') return fitment.year;
    if (key === 'make') return fitment.make;
    if (key === 'model') return fitment.model;
    if (key === 'submodel') return fitment.submodel || '-';
    return '-';
};

const getFitmentDisplayValue = (
    fitment: ProductDetails['fitments'][number] | DetectedProductFitment,
    field: FitmentField,
) => {
    const key = normalizeFitmentKey(field);
    // Prefer generic fieldValues. The legacy columns are only a fallback for
    // Shopify-tag-derived rows and older product fitment responses.
    if ('fieldValues' in fitment && fitment.fieldValues?.[key]) {
        return fitment.fieldValues[key];
    }

    return getProductFitmentFieldValue(fitment, getLegacyFieldStateKey(field));
};

const parseYearValue = (value: string): number | null => {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

const isCanonicalVehicleYear = (value: string) => /^(?:19|20)\d{2}$/.test(value.trim());
const canonicalVehicleYearError = 'Vehicle years must be between 1900 and 2099.';

const getNextYearRangeStart = (ranges: YearRangeDraft[]) => {
    const years = ranges.flatMap((range) => [range.start, range.end])
        .filter(isCanonicalVehicleYear)
        .map(Number);
    const nextYear = years.length > 0 ? Math.max(...years) + 1 : new Date().getFullYear();
    return String(Math.min(2099, Math.max(1900, nextYear)));
};

const compareYearDescending = (leftValue: string, rightValue: string) => {
    const leftYear = parseYearValue(leftValue);
    const rightYear = parseYearValue(rightValue);

    if (leftYear != null && rightYear != null && leftYear !== rightYear) {
        return rightYear - leftYear;
    }

    if (leftYear != null && rightYear == null) {
        return -1;
    }

    if (leftYear == null && rightYear != null) {
        return 1;
    }

    return rightValue.localeCompare(leftValue, undefined, { numeric: true, sensitivity: 'base' });
};

const compareYearAscending = (leftValue: string, rightValue: string) =>
    -compareYearDescending(leftValue, rightValue);

const compareYearByConfiguredDirection = (
    leftValue: string,
    rightValue: string,
    yearField?: FitmentField,
) =>
    yearField?.sortDirection === 'Asc'
        ? compareYearAscending(leftValue, rightValue)
        : compareYearDescending(leftValue, rightValue);

const compareFitmentValueAscending = (leftValue: string, rightValue: string) =>
    leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' });

const getFitmentNotesSignature = (fitment: AssignedFitment) =>
    (fitment.notes ?? [])
        .map((note) => [
            note.text,
            note.source,
            note.qualifierId ?? '',
            note.sortOrder,
        ].join('\u001f'))
        .join('\u001e');

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsForPdp = (fields: FitmentField[], yearField?: FitmentField) => {
    if (!yearField) {
        return fields;
    }

    const makeField = fields.find((field) => isStandardFitmentField(field, 'make'));
    const modelField = fields.find((field) => isStandardFitmentField(field, 'model'));
    const prioritizedFields = [makeField, modelField].filter((field): field is FitmentField => !!field);
    const prioritizedSet = new Set(prioritizedFields);

    return [
        ...prioritizedFields,
        ...fields.filter((field) => field !== yearField && !prioritizedSet.has(field)),
        yearField,
    ];
};

const sortAssignedFitments = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
    yearField?: FitmentField,
) =>
    [...fitments].sort((left, right) => {
        for (const field of fields.filter((field) => field !== yearField)) {
            const comparison = compareFitmentValueAscending(
                getFitmentDisplayValue(left, field),
                getFitmentDisplayValue(right, field),
            );
            if (comparison !== 0) {
                return comparison;
            }
        }

        if (yearField) {
            const yearComparison = compareYearByConfiguredDirection(
                getFitmentDisplayValue(left, yearField),
                getFitmentDisplayValue(right, yearField),
                yearField,
            );
            if (yearComparison !== 0) {
                return yearComparison;
            }
        }

        return getFitmentIdentity(left).localeCompare(getFitmentIdentity(right), undefined, { numeric: true });
    });

const formatYearRange = (values: string[]) => {
    const uniqueValues = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
    if (uniqueValues.length === 0) {
        return '';
    }

    const numericYears = uniqueValues
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value))
        .sort((left, right) => left - right);
    const nonNumericYears = uniqueValues
        .filter((value) => !/^\d+$/.test(value))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));

    const ranges: string[] = [];
    if (numericYears.length > 0) {
        let rangeStart = numericYears[0];
        let previousYear = numericYears[0];

        numericYears.slice(1).forEach((year) => {
            if (year === previousYear + 1) {
                previousYear = year;
                return;
            }

            ranges.push(rangeStart === previousYear ? `${rangeStart}` : `${rangeStart}-${previousYear}`);
            rangeStart = year;
            previousYear = year;
        });

        ranges.push(rangeStart === previousYear ? `${rangeStart}` : `${rangeStart}-${previousYear}`);
    }

    return [...ranges, ...nonNumericYears].join(', ');
};

const buildYearRangeDrafts = (values: string[]): YearRangeDraft[] => {
    const uniqueValues = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
    const numericYears = uniqueValues
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value))
        .sort((left, right) => left - right);
    const nonNumericYears = uniqueValues
        .filter((value) => !/^\d+$/.test(value))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const ranges: YearRangeDraft[] = [];

    if (numericYears.length > 0) {
        let rangeStart = numericYears[0];
        let previousYear = numericYears[0];

        numericYears.slice(1).forEach((year) => {
            if (year === previousYear + 1) {
                previousYear = year;
                return;
            }

            ranges.push({
                id: `${rangeStart}-${previousYear}-${ranges.length}`,
                start: `${rangeStart}`,
                end: `${previousYear}`,
            });
            rangeStart = year;
            previousYear = year;
        });

        ranges.push({
            id: `${rangeStart}-${previousYear}-${ranges.length}`,
            start: `${rangeStart}`,
            end: `${previousYear}`,
        });
    }

    nonNumericYears.forEach((year) => {
        ranges.push({
            id: `${year}-${ranges.length}`,
            start: year,
            end: year,
        });
    });

    return ranges.length > 0 ? ranges : [{ id: 'range-0', start: '', end: '', autoFillEnd: true }];
};

const expandYearRangeDrafts = (ranges: YearRangeDraft[]) => {
    const years: string[] = [];
    const seenYears = new Set<string>();

    for (const range of ranges) {
        const start = range.start.trim();
        const end = range.end.trim();
        if (!start || !end) {
            return { years: [], error: 'Enter a start and end year for each range.' };
        }

        const startYear = parseYearValue(start);
        const endYear = parseYearValue(end);
        if (startYear == null || endYear == null) {
            return { years: [], error: 'Year ranges must use whole-number years.' };
        }

        if (!isCanonicalVehicleYear(start) || !isCanonicalVehicleYear(end)) {
            return { years: [], error: canonicalVehicleYearError };
        }

        if (startYear > endYear) {
            return { years: [], error: 'Start year must be before or equal to end year.' };
        }

        for (let year = startYear; year <= endYear; year += 1) {
            const yearValue = `${year}`;
            if (seenYears.has(yearValue)) {
                return { years: [], error: 'Year ranges cannot overlap.' };
            }

            seenYears.add(yearValue);
            years.push(yearValue);
        }
    }

    return { years, error: '' };
};

const buildIndividualYearFitmentRows = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
): AssignedFitmentRow[] =>
    fitments.map((fitment) => ({
        key: getFitmentIdentity(fitment),
        fitments: [fitment],
        fieldValues: fields.reduce<Record<string, string>>((values, field) => {
            values[normalizeFitmentKey(field)] = getFitmentDisplayValue(fitment, field);
            return values;
        }, {}),
    }));

const buildGroupedFitmentRows = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
    yearField: FitmentField,
): AssignedFitmentRow[] => {
    const groupedRows = new Map<string, AssignedFitment[]>();
    fitments.forEach((fitment) => {
        const groupKey = [
            // Hidden custom values also distinguish assignments; never merge
            // different vehicles merely because those fields aren't displayed.
            ...Object.entries(fitment.fieldValues ?? {}).filter(([key]) => key !== 'year')
                .sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => `${key}=${value}`),
            `notes=${getFitmentNotesSignature(fitment)}`,
        ].join('\u001e');

        const group = groupedRows.get(groupKey) ?? [];
        group.push(fitment);
        groupedRows.set(groupKey, group);
    });

    return Array.from(groupedRows.values()).map((group) => {
        const firstFitment = group[0];
        const fieldValues = fields.reduce<Record<string, string>>((values, field) => {
            const key = normalizeFitmentKey(field);
            values[key] = key === 'year'
                ? formatYearRange(group.map((fitment) => getFitmentDisplayValue(fitment, yearField)))
                : getFitmentDisplayValue(firstFitment, field);
            return values;
        }, {});

        return {
            key: group.length === 1
                ? getFitmentIdentity(firstFitment)
                : `group:${group.map(getFitmentIdentity).join('|')}`,
            fitments: group,
            fieldValues,
        };
    });
};

const getAddFitmentYearValues = (startValue: string, endValue: string) => {
    const start = startValue.trim();
    const end = endValue.trim();

    if (!start && !end) {
        return { values: [''], error: '' };
    }

    if (!start) {
        return { values: [], error: 'Choose a starting year before choosing Year To.' };
    }

    const startYear = parseYearValue(start);
    if (startYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (!isCanonicalVehicleYear(start)) {
        return { values: [], error: canonicalVehicleYearError };
    }

    if (!end || end === start) {
        return { values: [start], error: '' };
    }

    const endYear = parseYearValue(end);
    if (endYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (!isCanonicalVehicleYear(end)) {
        return { values: [], error: canonicalVehicleYearError };
    }

    if (startYear > endYear) {
        return { values: [], error: 'Year To must be the same as or after the starting year.' };
    }

    return {
        values: Array.from({ length: endYear - startYear + 1 }, (_, index) => `${startYear + index}`),
        error: '',
    };
};

const getAutocompleteOptions = (options: SelectOption[], value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const usableOptions = options.filter((option) => option.value);
    const matchingOptions = normalizedValue
        ? usableOptions.filter((option) =>
            option.label.toLowerCase().includes(normalizedValue)
            || option.value.toLowerCase().includes(normalizedValue))
        : usableOptions;

    return matchingOptions;
};

const FitmentValueAutocomplete = ({
    label,
    value,
    options,
    disabled,
    loading,
    error,
    placeholder,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    placeholder?: string;
    onChange: (value: string, immediate: boolean) => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const filteredOptions = getAutocompleteOptions(options, value);

    useEffect(() => {
        containerRef.current
            ?.querySelectorAll<HTMLButtonElement>('.Polaris-TextField__ClearButton')
            .forEach((button) => {
                // Polaris clear buttons are focusable by default. Keeping them
                // out of tab order lets the combobox close before focus moves
                // to the next selector input.
                button.tabIndex = -1;
            });
    }, [value]);

    const textField = (
        <Autocomplete.TextField
            label={label}
            value={value}
            onChange={(text) => onChange(text, false)}
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            clearButton={!!value}
            onClearButtonClick={() => onChange('', true)}
            error={error ? true : undefined}
        />
    );

    return (
        <div ref={containerRef}>
            <Autocomplete
                options={filteredOptions}
                selected={value ? [value] : []}
                textField={textField}
                onSelect={(selected) => onChange(selected[0] ?? '', true)}
                loading={loading}
                emptyState={(
                    <div style={{ padding: 12 }}>
                        <Text as="p" variant="bodySm" tone="subdued">
                            No saved values. Type a new value to use it.
                        </Text>
                    </div>
                )}
            />
        </div>
    );
};

const ProductDetailsPage: React.FC = () => {
    const { id } = useParams();
    const productId = Number(id);
    const navigate = useNavigate();
    const { userStore } = useStore();
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const [catalogLoaded, setCatalogLoaded] = useState(false);

    const [product, setProduct] = useState<ProductDetails | null>(null);
    const [editDraft, setEditDraft] = useState(() => new FitmentDraft());
    const editDraftRef = useRef(editDraft);
    const receiveDraft = useCallback((next: FitmentDraft) => { editDraftRef.current = next; setEditDraft(next); }, []);
    const draftFitments = useMemo(() => editDraft.visible(), [editDraft]);
    const [reviewChanges, setReviewChanges] = useState(false);
    const [syncRecoveryId, setSyncRecoveryId] = useState<string | null>(null);
    const [saveAttempt, setSaveAttempt] = useState<Parameters<typeof agent.Products.saveFitmentChanges>[1] | null>(null);
    const [targetVariantIds, setTargetVariantIds] = useState<number[]>([]);
    const [variantPickerOpen, setVariantPickerOpen] = useState(false);
    const [variantReview, setVariantReview] = useState<{ review: VariantFitmentReview; attempt: SaveFitmentChangesPayload } | null>(null);
    const setDraftFitments = useCallback((update: React.SetStateAction<ProductDetails['fitments']>) => {
        try {
            const current = editDraftRef.current;
            receiveDraft(current.stage(typeof update === 'function' ? update(current.visible()) : update));
            return true;
        } catch (e) { setError((e as Error).message); return false; }
    }, [receiveDraft]);
    const scopeKeyRef = useRef('');
    const pageRequestRef = useRef(0);
    const detailsRequestRef = useRef(0);
    const detailsAbortRef = useRef<AbortController>();
    const resolvedScopeRef = useRef<{ productId: number; variantId: number | null }>();
    const pageAbortRef = useRef<AbortController>();
    const [detailsReady, setDetailsReady] = useState(false);
    const [detailsRevision, setDetailsRevision] = useState(0);
    const [fitmentPageReady, setFitmentPageReady] = useState(false);
    const [scopeAction, setScopeAction] = useState<{ operation: string; count: number; version: string } | null>(null);
    const [draftIsUniversal, setDraftIsUniversal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [assignedFitmentPageLoading, setAssignedFitmentPageLoading] = useState(false);
    const [assignedFitmentPageError, setAssignedFitmentPageError] = useState('');
    const requestedFitmentPageRef = useRef(1);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>('');
    const [useYmmPrefixForProductTags, setUseYmmPrefixForProductTags] = useState(true);
    const [showRawTags, setShowRawTags] = useState(false);
    const [fitmentActionsOpen, setFitmentActionsOpen] = useState(false);
    const [copyQuery, setCopyQuery] = useState('');
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copySourceProducts, setCopySourceProducts] = useState<RealtimeProduct[]>([]);
    const [copySearching, setCopySearching] = useState(false);
    const [includeCopiedFitmentNotes, setIncludeCopiedFitmentNotes] = useState(true);
    const [copySourceSelection, setCopySourceSelection] = useState<CopySourceSelection | null>(null);
    const [copyJobs, setCopyJobs] = useState<ProductFitmentCopyJob[]>([]);
    const [copyHistoryOpen, setCopyHistoryOpen] = useState(false);
    const copyJobStatesRef = useRef<Map<number, string>>(new Map());
    const [assignedFitmentView, setAssignedFitmentView] = useState<AssignedFitmentView>('grouped');
    const [assignedFitmentSearch, setAssignedFitmentSearch] = useState('');
    const [debouncedFitmentSearch, setDebouncedFitmentSearch] = useState('');
    const [editingFitment, setEditingFitment] = useState<FitmentEditDraft | null>(null);
    const fitmentNotesRef = useRef<HTMLDivElement>(null);
    const [fitmentEditError, setFitmentEditError] = useState('');
    const [editOptionsDelay, setEditOptionsDelay] = useState(0);

    const [selectedFitmentValues, setSelectedFitmentValues] = useState<FitmentFieldValues>({});
    const [selectedFitmentYearTo, setSelectedFitmentYearTo] = useState('');
    const [newFitmentNoteLines, setNewFitmentNoteLines] = useState<string[]>([]);
    const [showNewFitmentNotes, setShowNewFitmentNotes] = useState(false);
    const [optionsDelay, setOptionsDelay] = useState(0);
    const [optionsRevision, setOptionsRevision] = useState(0);
    // These dependencies invalidate page-owned options when their scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const optionsCache = useMemo(() => new FitmentOptionsCache(), [productId, userStore.user?.shopDomain, fitmentCatalog, optionsRevision]);
    const activeCopyJob = copyJobs.find((job) => job.isActive);
    const copyJobRunning = !!activeCopyJob;
    const visibleFitmentFields = useMemo(() => getVisibleFitmentFields(fitmentCatalog), [fitmentCatalog]);
    const visibleFitmentFieldKey = useMemo(
        () => visibleFitmentFields.map((field) => normalizeFitmentKey(field)).join('|'),
        [visibleFitmentFields],
    );
    const standardYearFitmentField = useMemo(
        () => visibleFitmentFields.find((field) => isStandardFitmentField(field, 'year')),
        [visibleFitmentFields],
    );
    const productDetailsFitmentFields = useMemo(
        () => orderFitmentFieldsForPdp(visibleFitmentFields, standardYearFitmentField),
        [standardYearFitmentField, visibleFitmentFields],
    );
    const editableFitmentFields = useMemo(
        () => productDetailsFitmentFields.filter((field) => field !== standardYearFitmentField),
        [productDetailsFitmentFields, standardYearFitmentField],
    );
    const editingFitmentValues = editingFitment?.fieldValues;
    const { options: fieldOptions, loading: loadingFieldOptions } = useFitmentOptions(
        optionsCache, productDetailsFitmentFields, selectedFitmentValues, catalogLoaded && !draftIsUniversal, optionsDelay);
    const { options: editFieldOptions, loading: loadingEditFieldOptions } = useFitmentOptions(
        optionsCache, productDetailsFitmentFields, editingFitmentValues, catalogLoaded, editOptionsDelay, editableFitmentFields);
    // The Add button follows the merchant's required-field setup. In a custom
    // catalog with no required fields, one selected value is enough to add.
    const hasVehicleContext = Boolean(
        visibleFitmentFields.length > 0
        && (visibleFitmentFields.some((field) => field.isRequired)
            ? hasRequiredFitmentFieldValues(visibleFitmentFields, selectedFitmentValues)
            : hasAnyFitmentFieldValue(visibleFitmentFields, selectedFitmentValues))
    );

    const applyFitmentPage = useCallback((result: ProductFitmentPage) => {
        setProduct((current) => current
            ? {
                ...current,
                fitmentCount: result.fitmentCount,
                totalRows: result.totalRows,
                fitmentLoadLimit: result.fitmentLoadLimit,
                fitmentsTruncated: result.fitmentsTruncated,
                fitmentPage: result.fitmentPage,
                fitmentPageSize: result.fitmentPageSize,
                fitmentHasNext: result.fitmentHasNext,
                fitmentHasPrevious: result.fitmentHasPrevious,
                fitments: result.fitments || [],
            }
            : current);
        receiveDraft(editDraftRef.current.receive(result.fitments || []));
        setEditingFitment(null);
        setFitmentEditError('');
    }, [receiveDraft]);

    const loadedProductId = product?.id;
    const loadedVariantId = product?.selectedVariantId ?? 0;
    const loadAssignedFitmentPage = useCallback(async (page: number) => {
        if (!detailsReady || !catalogLoaded || loadedProductId !== productId) return;
        const request = ++pageRequestRef.current;
        pageAbortRef.current?.abort();
        const controller = new AbortController();
        pageAbortRef.current = controller;
        requestedFitmentPageRef.current = page;
        setAssignedFitmentPageError('');
        setAssignedFitmentPageLoading(true);
        setFitmentPageReady(false);
        try {
            const result = await agent.Products.fitments(loadedProductId, loadedVariantId,
                page, assignedFitmentPageSize, debouncedFitmentSearch, !!standardYearFitmentField && assignedFitmentView === 'grouped', controller.signal);
            if (request === pageRequestRef.current) {
                applyFitmentPage(result);
                setFitmentPageReady(true);
            }
        } catch (e: any) {
            if (request === pageRequestRef.current && e?.code !== 'ERR_CANCELED') {
                setAssignedFitmentPageError('Assigned fitments could not be loaded. Please retry.');
            }
        } finally {
            if (request === pageRequestRef.current) setAssignedFitmentPageLoading(false);
        }
    }, [applyFitmentPage, loadedProductId, loadedVariantId, productId, detailsReady, catalogLoaded, debouncedFitmentSearch, standardYearFitmentField, assignedFitmentView]);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedFitmentSearch(assignedFitmentSearch), 250);
        return () => window.clearTimeout(timer);
    }, [assignedFitmentSearch]);

    useEffect(() => {
        const sequence = pageRequestRef;
        void loadAssignedFitmentPage(1);
        return () => { sequence.current++; pageAbortRef.current?.abort(); };
    }, [loadAssignedFitmentPage, detailsRevision]);

    const loadDetails = useCallback(async (requestedScope?: string) => {
        if (!productId || Number.isNaN(productId)) {
            setError('Invalid product id.');
            return;
        }

        const request = ++detailsRequestRef.current;
        detailsAbortRef.current?.abort();
        const controller = new AbortController();
        detailsAbortRef.current = controller;
        ++pageRequestRef.current;
        pageAbortRef.current?.abort();
        setLoading(true);
        setDetailsReady(false);
        setFitmentPageReady(false);
        setAssignedFitmentPageLoading(false);
        setAssignedFitmentPageError('');
        setError('');
        try {
            const requestedVariantId = requestedScope === productScopeValue
                ? 0
                : requestedScope !== undefined ? getVariantIdFromScope(requestedScope)
                    : resolvedScopeRef.current?.productId === productId ? resolvedScopeRef.current.variantId ?? 0 : undefined;
            // Resolve the scope once and load only the header here. The table
            // effect owns page loading; neither automatic scope selection nor
            // table navigation should repeat the Shopify product refresh.
            const result = await agent.Products.details(productId, requestedVariantId, 1, assignedFitmentPageSize, true, false, controller.signal);
            if (request !== detailsRequestRef.current) return;
            // Details can import a previously uncached Shopify product. Check
            // recovery only after that refresh, so authorization sees the row.
            const pending = await agent.Products.pendingFitmentSave(productId);
            if (request !== detailsRequestRef.current) return;
            setSyncRecoveryId(pending.id);
            setProduct(result);
            const scopeKey = String(productId) + ':' + String(result.selectedVariantId ?? 0);
            if (scopeKeyRef.current !== scopeKey) {
                setTargetVariantIds([]);
                setVariantReview(null);
                setVariantPickerOpen(false);
            }
            receiveDraft(scopeKeyRef.current === scopeKey ? editDraftRef.current.receive([]) : new FitmentDraft());
            scopeKeyRef.current = scopeKey;
            resolvedScopeRef.current = { productId, variantId: result.selectedVariantId ?? null };
            setEditingFitment(null);
            setFitmentEditError('');
            setDraftIsUniversal(result.selectedVariantId ? false : !!result.isUniversal);
            setDetailsReady(true);
            setDetailsRevision((revision) => revision + 1);
        } catch (e: any) {
            if (request === detailsRequestRef.current && e?.code !== 'ERR_CANCELED') setError(e?.message ?? 'Failed to load product details.');
        } finally {
            if (request === detailsRequestRef.current) setLoading(false);
        }
    }, [productId, receiveDraft]);

    useEffect(() => {
        loadDetails();
        const sequence = detailsRequestRef;
        return () => { sequence.current++; detailsAbortRef.current?.abort(); };
    }, [loadDetails]);

    const loadCopyJobs = useCallback(async (signal: AbortSignal) => {
        if (!productId || Number.isNaN(productId)) return [] as ProductFitmentCopyJob[];

        try {
            const nextJobs = await agent.Products.listFitmentCopyJobs(productId, 10, { recentOnly: true, signal });
            if (signal.aborted) return [];
            const completedWhileOpen = nextJobs.some((job) => {
                const previousState = copyJobStatesRef.current.get(job.id);
                return (previousState === 'Queued' || previousState === 'Processing')
                    && (job.state === 'Completed' || job.state === 'CompletedWithWarnings');
            }) || Array.from(copyJobStatesRef.current).some(([id, state]) =>
                (state === 'Queued' || state === 'Processing') && !nextJobs.some((job) => job.id === id));

            copyJobStatesRef.current = new Map(nextJobs.map((job) => [job.id, job.state]));
            setCopyJobs(nextJobs);
            if (completedWhileOpen) {
                setOptionsRevision((revision) => revision + 1);
                await loadDetails();
            }
            return nextJobs;
        } catch {
            // Copy actions surface errors directly. Polling failures remain quiet
            // so a temporary network issue does not replace the product content.
            return [] as ProductFitmentCopyJob[];
        }
    }, [loadDetails, productId]);

    useEffect(() => {
        let disposed = false;
        let timeoutId: number | undefined;
        const controller = new AbortController();
        setCopyJobs([]);
        setCopyHistoryOpen(false);
        copyJobStatesRef.current.clear();

        const poll = async () => {
            const jobs = await loadCopyJobs(controller.signal);
            if (disposed) return;
            timeoutId = window.setTimeout(poll, jobs.some((job) => job.isActive) ? 2500 : 10000);
        };

        poll();
        return () => {
            disposed = true;
            controller.abort();
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        };
    }, [loadCopyJobs]);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await agent.Settings.details();
                setUseYmmPrefixForProductTags(settings.useYmmPrefixForProductTags ?? true);
            } catch {
                setUseYmmPrefixForProductTags(true);
            }
        };

        loadSettings();
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) setFitmentCatalog(catalog);
            } catch {
                if (!cancelled) setFitmentCatalog(null);
            } finally {
                if (!cancelled) setCatalogLoaded(true);
            }
        };

        loadFitmentCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setSelectedFitmentValues((current) => normalizeFitmentFieldValues(visibleFitmentFields, current));
    }, [visibleFitmentFieldKey, visibleFitmentFields]);

    const selectedFitmentYearValue = standardYearFitmentField
        ? getFitmentFieldValue(selectedFitmentValues, standardYearFitmentField)
        : '';
    useEffect(() => {
        // Year to is separate from the catalog fields, but must reset with the
        // starting year when an earlier vehicle selection changes or is cleared.
        if (!selectedFitmentYearValue.trim()) setSelectedFitmentYearTo('');
    }, [selectedFitmentYearValue]);

    const addFitmentYearRange = useMemo(
        () => standardYearFitmentField
            ? getAddFitmentYearValues(selectedFitmentYearValue, selectedFitmentYearTo)
            : { values: [''], error: '' },
        [selectedFitmentYearTo, selectedFitmentYearValue, standardYearFitmentField],
    );
    const canAddFitment = hasVehicleContext && !addFitmentYearRange.error;

    const onAddFitment = useCallback(async () => {
        if (!canAddFitment) {
            return;
        }

        const normalizedValues = normalizeFitmentFieldValues(visibleFitmentFields, selectedFitmentValues);
        const yearKey = standardYearFitmentField ? normalizeFitmentKey(standardYearFitmentField) : '';
        const notes = buildManualFitmentNotes(newFitmentNoteLines);
        const yearValues = standardYearFitmentField
            ? addFitmentYearRange.values
            : [''];
        const fitmentsToAdd = yearValues.map((yearValue) => {
            const values = yearKey
                ? normalizeFitmentFieldValues(visibleFitmentFields, { ...normalizedValues, [yearKey]: yearValue })
                : normalizedValues;

            return buildDraftFitmentFromValues(values, visibleFitmentFields, useYmmPrefixForProductTags, notes);
        });

        const staged = setDraftFitments((currentFitments) => {
            const existingFieldValueKeys = new Set(currentFitments.map((fitment) => JSON.stringify(fitment.fieldValues ?? {})));
            const newFitments = fitmentsToAdd.filter((fitment) => {
                const key = JSON.stringify(fitment.fieldValues ?? {});
                if (existingFieldValueKeys.has(key)) {
                    return false;
                }

                existingFieldValueKeys.add(key);
                return true;
            });

            return newFitments.length > 0 ? [...currentFitments, ...newFitments] : currentFitments;
        });

        if (!staged) return;
        setSelectedFitmentValues({});
        setSelectedFitmentYearTo('');
        setNewFitmentNoteLines([]);
        setShowNewFitmentNotes(false);
    }, [setDraftFitments, addFitmentYearRange.values, canAddFitment, newFitmentNoteLines, selectedFitmentValues, standardYearFitmentField, useYmmPrefixForProductTags, visibleFitmentFields]);

    const onRemoveFitments = useCallback((fitmentKeys: string[], rowKey: string) => {
        const fitmentKeySet = new Set(fitmentKeys);
        setDraftFitments((currentFitments) =>
            currentFitments.filter((fitment) => !fitmentKeySet.has(getFitmentIdentity(fitment))));
        setEditingFitment((current) => current?.rowKey === rowKey ? null : current);
    }, [setDraftFitments]);

    const onOpenFitmentEditor = useCallback((row: AssignedFitmentRow, focusNotes = false) => {
        setEditOptionsDelay(0);
        const noteLines = getFitmentNoteLines(row.fitments[0]);
        setEditingFitment({
            rowKey: row.key,
            fitmentKeys: row.fitments.map(getFitmentIdentity),
            fieldValues: normalizeFitmentFieldValues(editableFitmentFields, row.fieldValues),
            ranges: standardYearFitmentField
                ? buildYearRangeDrafts(row.fitments.map((fitment) => getFitmentDisplayValue(fitment, standardYearFitmentField)))
                : [],
            noteLines: noteLines.length > 0 ? noteLines : [''],
            focusNotes,
        });
        setFitmentEditError('');
    }, [editableFitmentFields, standardYearFitmentField]);

    const onUpdateEditedFitmentField = useCallback((field: FitmentField, value: string, immediate: boolean) => {
        setEditOptionsDelay(immediate ? 0 : 200);
        setEditingFitment((current) => current && getFitmentFieldValue(current.fieldValues, field) !== value ? {
            ...current,
            fieldValues: updateFitmentFieldValue(editableFitmentFields, current.fieldValues, field, value),
        } : current);
        setFitmentEditError('');
    }, [editableFitmentFields]);

    const onUpdateYearGroupRange = useCallback((rangeId: string, field: 'start' | 'end', value: string, fromSpinner = false) => {
        setEditingFitment((current) => current
            ? {
                ...current,
                ranges: current.ranges.map((range) => {
                    if (range.id !== rangeId) return range;

                    const pairedYear = field === 'start' ? range.end : range.start;
                    const nextValue = fromSpinner && !isCanonicalVehicleYear(range[field])
                        ? isCanonicalVehicleYear(pairedYear)
                            ? pairedYear.trim()
                            : getNextYearRangeStart(current.ranges)
                        : value;

                    if (field === 'end') {
                        return { ...range, end: nextValue, autoFillEnd: false };
                    }

                    return {
                        ...range,
                        start: nextValue,
                        end: range.autoFillEnd && isCanonicalVehicleYear(nextValue) ? nextValue.trim() : range.end,
                    };
                }),
            }
            : current);
        setFitmentEditError('');
    }, []);

    const onAddYearGroupRange = useCallback(() => {
        setEditingFitment((current) => current
            ? {
                ...current,
                ranges: [
                    ...current.ranges,
                    { id: `range-${Date.now()}`, start: '', end: '', autoFillEnd: true },
                ],
            }
            : current);
        setFitmentEditError('');
    }, []);

    const onRemoveYearGroupRange = useCallback((rangeId: string) => {
        setEditingFitment((current) => current
            ? {
                ...current,
                ranges: current.ranges.length > 1
                    ? current.ranges.filter((range) => range.id !== rangeId)
                    : current.ranges,
            }
            : current);
        setFitmentEditError('');
    }, []);

    const onApplyFitmentEdit = useCallback(() => {
        if (!editingFitment) {
            return;
        }

        const missingFields = editableFitmentFields.filter((field) => field.isRequired && !getFitmentFieldValue(editingFitment.fieldValues, field).trim());
        if (missingFields.length > 0) {
            setFitmentEditError(`Enter the required fields: ${missingFields.map((field) => field.label).join(', ')}.`);
            return;
        }
        const oversizedField = editableFitmentFields.find((field) => getFitmentFieldValue(editingFitment.fieldValues, field).trim().length > Math.min(field.maxLength || 200, 200));
        if (oversizedField) {
            setFitmentEditError(`${oversizedField.label} must be at most ${Math.min(oversizedField.maxLength || 200, 200)} characters.`);
            return;
        }

        const optionalYearIsEmpty = standardYearFitmentField && !standardYearFitmentField.isRequired
            && editingFitment.ranges.length === 1 && !editingFitment.ranges[0].start.trim() && !editingFitment.ranges[0].end.trim();
        const result = standardYearFitmentField && !optionalYearIsEmpty ? expandYearRangeDrafts(editingFitment.ranges) : { years: [''], error: '' };
        if (result.error) {
            setFitmentEditError(result.error);
            return;
        }

        if (result.years.length === 0) {
            setFitmentEditError('Add at least one year range.');
            return;
        }

        const fitmentKeySet = new Set(editingFitment.fitmentKeys);
        const currentDraft = editDraftRef.current;
        const currentFitments = currentDraft.visible();
        const originalFitments = currentFitments.filter((fitment) => fitmentKeySet.has(getFitmentIdentity(fitment)));
        if (originalFitments.length !== fitmentKeySet.size) {
            setFitmentEditError('This row changed while you were editing. Close the editor and open it again.');
            return;
        }
        // Grouped rows share the complete note signature. Only an explicit note
        // change replaces imported metadata; untouched notes stay on each year.
        const notesChanged = JSON.stringify(normalizeFitmentNoteLines(editingFitment.noteLines))
            !== JSON.stringify(normalizeFitmentNoteLines(getFitmentNoteLines(originalFitments[0])));
        const editedNotes = notesChanged ? buildManualFitmentNotes(editingFitment.noteLines) : undefined;
        const replacementFitments = result.years.map((year) => {
            const original = originalFitments.find((fitment) => (fitment.fieldValues?.year ?? fitment.year) === year) ?? originalFitments[0];
            const values = { ...(original.fieldValues ?? { year: original.year, make: original.make, model: original.model, submodel: original.submodel }) };
            editableFitmentFields.forEach((field) => {
                const key = normalizeFitmentKey(field);
                const value = getFitmentFieldValue(editingFitment.fieldValues, field).trim();
                if (value) values[key] = value;
                else delete values[key];
            });
            if (standardYearFitmentField) values.year = year;
            const replacement = buildDraftFitmentFromValues(values, visibleFitmentFields, useYmmPrefixForProductTags, editedNotes ?? original.notes, 'Manual', true);
            const existing = originalFitments.find((fitment) => fitmentKey(fitment) === fitmentKey(replacement));
            return existing ? editedNotes ? { ...existing, notes: editedNotes } : existing : replacement;
        });
        if (replacementFitments.some((fitment) => !hasAnyFitmentFieldValue(visibleFitmentFields, fitment.fieldValues ?? {}))) {
            setFitmentEditError('Enter at least one fitment value.');
            return;
        }
        const remainingFitments = currentFitments.filter((fitment) => !fitmentKeySet.has(getFitmentIdentity(fitment)));
        const existingKeys = new Set([
            ...remainingFitments.map(fitmentKey),
            ...Array.from(currentDraft.changes.values()).flatMap((change) => change.after && !fitmentKeySet.has(getFitmentIdentity(change.after)) ? [fitmentKey(change.after)] : []),
        ]);
        if (replacementFitments.some((fitment) => existingKeys.has(fitmentKey(fitment)))) {
            setFitmentEditError('This fitment is already assigned on another row. Edit that row or choose different values.');
            return;
        }

        try {
            receiveDraft(currentDraft.replace(originalFitments, replacementFitments, [...remainingFitments, ...replacementFitments]));
        } catch (e) {
            setFitmentEditError((e as Error).message);
            return;
        }
        setEditingFitment(null);
        setFitmentEditError('');
    }, [editableFitmentFields, editingFitment, receiveDraft, standardYearFitmentField, useYmmPrefixForProductTags, visibleFitmentFields]);

    const onDiscardChanges = useCallback(() => {
        receiveDraft(new FitmentDraft(product?.fitments || []));
        setTargetVariantIds([]);
        setVariantReview(null);
        setDraftIsUniversal(product?.selectedVariantId ? false : !!product?.isUniversal);
        setEditingFitment(null);
        setFitmentEditError('');
        setError('');
    }, [receiveDraft, product?.fitments, product?.isUniversal, product?.selectedVariantId]);

    const prepareScopeAction = useCallback(async (operation: string) => {
        if (editDraftRef.current.changes.size > 0 || saveAttempt || syncRecoveryId) {
            setError('Save or discard your pending changes before this action.'); return;
        }
        setSubmitting(true);
        try {
            const summary = await agent.Products.fitmentEditSummary(productId, product?.selectedVariantId);
            setScopeAction({ operation, ...summary });
        } catch (e) { setError((e as Error).message); }
        finally { setSubmitting(false); }
    }, [productId, product?.selectedVariantId, saveAttempt, syncRecoveryId]);
    const onToggleUniversal = (checked: boolean) => prepareScopeAction(checked ? 'universal' : 'specific');

    const onSearchCopySource = useCallback(async () => {
        if (!copyQuery.trim()) {
            setCopySourceProducts([]);
            return;
        }

        setCopySearching(true);
        setError('');
        try {
            const response = await agent.Products.listRealtime(copyQuery.trim(), '', 20);
            setCopySourceProducts(
                response.products.filter((candidate: RealtimeProduct) => candidate.id !== productId),
            );
        } catch (e: any) {
            setError(e?.message ?? 'Failed to search products.');
            setCopySourceProducts([]);
        } finally {
            setCopySearching(false);
        }
    }, [copyQuery, productId]);

    useEffect(() => {
        if (!copyModalOpen) {
            return;
        }

        if (!copyQuery.trim()) {
            setCopySourceProducts([]);
            setCopySearching(false);
            return;
        }

        // Debounce the modal search so typing feels responsive without firing a
        // realtime Shopify query on every keypress.
        const timeoutId = window.setTimeout(() => {
            onSearchCopySource();
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [copyModalOpen, copyQuery, onSearchCopySource]);

    const onOpenCopyModal = useCallback(() => {
        setIncludeCopiedFitmentNotes(true);
        setCopySourceSelection(null);
        setCopyModalOpen(true);
    }, []);

    const onCloseCopyModal = useCallback(() => {
        setCopyModalOpen(false);
        setCopySourceSelection(null);
    }, []);

    const onReviewCopySource = useCallback(async (sourceProduct: RealtimeProduct) => {
        setSubmitting(true);
        setError('');
        try {
            // Copy from the whole source product, including assignments stored
            // on its variants. The API de-duplicates identical vehicle records
            // and omits the inline payload when the background worker is needed.
            const source = await agent.Products.fitmentCopySource(sourceProduct.id);
            if (source.isUniversal) {
                setError('Cannot copy fitments from a universal product.');
                return;
            }
            setCopySourceSelection({ product: sourceProduct, source });
        } catch (e: any) {
            setError(e?.message ?? 'Failed to load fitments from the selected product.');
        } finally {
            setSubmitting(false);
        }
    }, []);

    const onConfirmCopyFitments = useCallback(async () => {
        if (!copySourceSelection) {
            return;
        }

        const { product: sourceProductDetails } = copySourceSelection;
        setSubmitting(true);
        setError('');
        try {
            const job = await agent.Products.createFitmentCopyJob(productId, sourceProductDetails.id, product?.selectedVariantId ?? null, includeCopiedFitmentNotes);
            setCopyJobs(current => [job, ...current.filter(item => item.id !== job.id)].slice(0, 10));
            copyJobStatesRef.current.set(job.id, job.state);
            onCloseCopyModal();
        } catch (e: any) {
            setError(e?.message ?? 'Failed to copy fitments from the selected product.');
        } finally {
            setSubmitting(false);
        }
    }, [copySourceSelection, includeCopiedFitmentNotes, onCloseCopyModal, product, productId]);

    const onRetryCopyJob = useCallback(async (job: ProductFitmentCopyJob) => {
        setSubmitting(true);
        setError('');
        try {
            const retriedJob = await agent.Products.retryFitmentCopyJob(productId, job.id);
            setCopyJobs((current) => [retriedJob, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
            copyJobStatesRef.current.set(retriedJob.id, retriedJob.state);
            return retriedJob;
        } catch (e: any) {
            setError(e?.message ?? 'Failed to retry the fitment copy.');
        } finally {
            setSubmitting(false);
        }
    }, [productId]);

    const submitChanges = useCallback(async (attempt: Parameters<typeof agent.Products.saveFitmentChanges>[1]) => {
        setSubmitting(true);
        setSaveAttempt(attempt);
        setError('');
        try {
            const result = await agent.Products.saveFitmentChanges(productId, attempt);
            setOptionsRevision((revision) => revision + 1);
            if (result.syncPending) {
                setSaveAttempt(null);
                receiveDraft(new FitmentDraft());
                setSyncRecoveryId(attempt.mutationId);
                setError(result.message);
                setScopeAction(null);
                await loadDetails();
                return;
            }
            setSaveAttempt(null);
            setScopeAction(null);
            setVariantReview(null);
            setTargetVariantIds([]);
            receiveDraft(new FitmentDraft());
            await loadDetails();
        } catch (e: any) {
            if (e?.response?.status === 400 || e?.response?.status === 409 || e?.status === 400 || e?.status === 409) {
                setSaveAttempt(null);
                setVariantReview(null);
            }
            setError(await getApiErrorMessage(e, 'Save could not be confirmed. Retry Save; your changes are retained.'));
        } finally { setSubmitting(false); }
    }, [productId, receiveDraft, loadDetails]);
    const checkAndSaveVariantChanges = async (attempt: SaveFitmentChangesPayload) => {
        setVariantReview(null);
        if (!attempt.targetVariantIds?.length) return submitChanges(attempt);
        setSubmitting(true);
        setError('');
        try {
            const review = await agent.Products.previewVariantFitmentChanges(productId, attempt);
            const checkedAttempt = { ...attempt, reviewVersion: review.version };
            if (!review.canSave || review.variants.some(variant => variantMissingMatches(variant) > 0)) {
                setVariantReview({ review, attempt: checkedAttempt });
            } else {
                await submitChanges(checkedAttempt);
            }
        } catch (e) { setError(await getApiErrorMessage(e, 'Could not check the changes. Your draft is retained.')); }
        finally { setSubmitting(false); }
    };
    const excludeConflictingVariants = async (ids: number[]) => {
        if (!variantReview || submitting || saveAttempt) return;
        const remaining = (variantReview.attempt.targetVariantIds ?? []).filter(id => !ids.includes(id));
        setTargetVariantIds(remaining);
        await checkAndSaveVariantChanges({ ...variantReview.attempt, targetVariantIds: remaining,
            mutationId: crypto.randomUUID(), reviewVersion: undefined });
    };
    const onSaveChanges = async () => {
        if (submitting || variantReview || targetVariantIds.length > 100) return;
        setVariantPickerOpen(false);
        if (syncRecoveryId) {
            setSubmitting(true);
            try {
                const result = await agent.Products.retryFitmentSave(productId, syncRecoveryId);
                if (result.syncPending) setError(result.message);
                else { setSyncRecoveryId(null); setError(''); await loadDetails(); }
            } catch (e) { setError(await getApiErrorMessage(e, 'Shopify synchronization could not finish. Retry Save.')); }
            finally { setSubmitting(false); }
            return;
        }
        if (saveAttempt) return submitChanges(saveAttempt);
        const attempt: SaveFitmentChangesPayload = {
            mutationId: crypto.randomUUID(), variantId: product?.selectedVariantId,
            changes: editDraftRef.current.payload(targetVariantIds.length > 0),
            ...(targetVariantIds.length ? { targetVariantIds } : {}),
        };
        return checkAndSaveVariantChanges(attempt);
    };

    const onImportDetectedFitments = useCallback(() => {
        if (!product?.detectedFitments?.length) {
            return;
        }

        setDraftFitments((currentFitments) => {
            const currentTags = new Set(currentFitments.map((fitment) => fitment.tag.toLowerCase()));
            const importedFitments = product.detectedFitments
                .filter((fitment) => !currentTags.has(fitment.canonicalTag.toLowerCase()))
                .map((fitment) => ({
                    id: buildStableClientTagId(fitment.canonicalTag),
                    tag: fitment.canonicalTag,
                    year: fitment.year,
                    make: fitment.make,
                    model: fitment.model,
                    submodel: fitment.submodel,
                    fieldValues: {
                        year: fitment.year,
                        make: fitment.make,
                        model: fitment.model,
                        submodel: fitment.submodel,
                    },
                    sourceType: 'Detected Tag',
                    source: fitment.sourceTag,
                    canRemove: true,
                    createdAt: new Date().toISOString(),
                    notes: [],
                }));

            return [...currentFitments, ...importedFitments];
        });
    }, [product?.detectedFitments, setDraftFitments]);

    const filteredFitments = draftFitments;
    const sortedFitments = useMemo(
        () => sortAssignedFitments(filteredFitments, productDetailsFitmentFields, standardYearFitmentField),
        [filteredFitments, productDetailsFitmentFields, standardYearFitmentField],
    );
    const effectiveAssignedFitmentView: AssignedFitmentView = standardYearFitmentField ? assignedFitmentView : 'individual';
    const assignedFitmentRows = useMemo(
        () => effectiveAssignedFitmentView === 'grouped' && standardYearFitmentField
            ? buildGroupedFitmentRows(sortedFitments, productDetailsFitmentFields, standardYearFitmentField)
            : buildIndividualYearFitmentRows(sortedFitments, productDetailsFitmentFields),
        [effectiveAssignedFitmentView, sortedFitments, productDetailsFitmentFields, standardYearFitmentField],
    );
    const pendingFitmentRows = useMemo(() => {
        // Use the full draft, including changes on other pages and removed rows.
        // Reuse table grouping so notes and hidden vehicle values stay distinct.
        const fitments = sortAssignedFitments(
            Array.from(editDraft.changes.values(), (change) => (change.after ?? change.before)!),
            productDetailsFitmentFields, standardYearFitmentField,
        );
        return effectiveAssignedFitmentView === 'grouped' && standardYearFitmentField
            ? buildGroupedFitmentRows(fitments, productDetailsFitmentFields, standardYearFitmentField)
            : buildIndividualYearFitmentRows(fitments, productDetailsFitmentFields);
    }, [editDraft.changes, effectiveAssignedFitmentView, productDetailsFitmentFields, standardYearFitmentField]);
    const pendingFitmentSummary = `${pendingFitmentRows.length.toLocaleString()} ${pendingFitmentRows.length === 1 ? 'row' : 'rows'} edited · ${formatFitmentCountLabel(editDraft.changes.size)} affected`;
    const isAssignedFitmentsLoading = loading || assignedFitmentPageLoading || ((!product || !catalogLoaded || !fitmentPageReady) && !error && !assignedFitmentPageError);
    const hasAssignedFitmentRefinements = assignedFitmentSearch.trim().length > 0;
    // Keep search available after an empty result, including while clearing a
    // debounced query. A genuinely empty product needs no table toolbar.
    const showAssignedFitmentToolbar = (product?.fitmentCount ?? 0) > 0 || draftFitments.length > 0
        || hasAssignedFitmentRefinements || debouncedFitmentSearch.trim().length > 0;
    const draftTagSet = new Set(draftFitments.map((fitment) => fitment.tag.toLowerCase()));
    const selectedVariantId = product?.selectedVariantId ?? null;
    const isVariantScope = !!selectedVariantId;
    const selectedVariant = product?.variants?.find((variant) => variant.id === selectedVariantId);
    const copyTargetScopeLabel = selectedVariant
        ? `${selectedVariant.sku ? `SKU ${selectedVariant.sku}` : selectedVariant.title || `variant ${selectedVariant.id}`} variant`
        : 'product-level fitment scope';
    const fitmentsTruncated = !!product?.fitmentsTruncated;
    const copyTargetFitmentCount = fitmentsTruncated
        ? (product?.fitmentCount ?? draftFitments.length)
        : draftFitments.length;
    const copyRunsInBackground = !!copySourceSelection;
    const assignedFitmentCurrentPage = product?.fitmentPage ?? 1;
    const assignedFitmentEffectivePageSize = product?.fitmentPageSize || assignedFitmentPageSize;
    const assignedFitmentTotalPages = Math.max(1, Math.ceil((product?.totalRows ?? product?.fitmentCount ?? 0) / assignedFitmentEffectivePageSize));
    const variantsWithFitments = (product?.variants ?? []).filter((variant) => variant.fitmentCount > 0);
    const showFitmentScopeSelector = (product?.variants?.length ?? 0) > 1
        || ((product?.productLevelFitmentCount ?? 0) > 0 && variantsWithFitments.length > 0);
    const importableDetectedFitments = (product?.detectedFitments || [])
        .filter((fitment) => !draftTagSet.has(fitment.canonicalTag.toLowerCase()))
        .sort(sortDetectedFitments);
    const hasUnsavedChanges = editDraft.changes.size > 0 || !!saveAttempt || !!syncRecoveryId;
    const editingBlocked = submitting || loading || !detailsReady || !fitmentPageReady || assignedFitmentPageLoading || copyJobRunning || !!saveAttempt || !!syncRecoveryId;
    const blocker = useBlocker(hasUnsavedChanges);
    useEffect(() => {
        if (!hasUnsavedChanges) return;
        const preventLeave = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', preventLeave);
        return () => window.removeEventListener('beforeunload', preventLeave);
    }, [hasUnsavedChanges]);
    const latestFailedCopyJob = !activeCopyJob
        ? copyJobs.find((job) => job.state === 'Failed')
        : undefined;
    const fitmentScopeOptions = [
        {
            label: `Product-level fitments (${formatFitmentCountLabel(product?.productLevelFitmentCount ?? 0)})`,
            value: productScopeValue,
        },
        { title: 'Variants', options: (product?.variants ?? []).map((variant) => {
            let label = `Variant ${variant.id}`;
            if (variant.sku && variant.title) {
                label = `${variant.sku} — ${variant.title}`;
            } else if (variant.title) {
                label = variant.title;
            } else if (variant.sku) {
                label = `SKU ${variant.sku}`;
            }

            label = `${label} (${formatFitmentCountLabel(variant.fitmentCount)})`;

            return {
                label,
                value: getVariantScopeValue(variant.id),
            };
        }) },
    ];
    const selectedScopeValue = selectedVariantId ? getVariantScopeValue(selectedVariantId) : productScopeValue;
    const shopHandle = getShopifyStoreHandle(userStore.user?.shopDomain);
    const shopifyAdminProductUrl = shopHandle && product?.id
        ? `https://admin.shopify.com/store/${shopHandle}/products/${product.id}`
        : '';
    const fitmentSelectorConfigured = visibleFitmentFields.length > 0;
    return (
        <>
            <SaveBar id="product-fitments-save-bar" open={hasUnsavedChanges}>
                <button onClick={onDiscardChanges} disabled={submitting || loading || copyJobRunning || !!saveAttempt || !!syncRecoveryId}>
                    Discard
                </button>
                <button variant="primary" onClick={onSaveChanges} disabled={submitting || loading || copyJobRunning || !!variantReview || targetVariantIds.length > 100} loading={submitting}>
                    {targetVariantIds.length ? `Save to ${targetVariantIds.length + 1} variants` : 'Save'}
                </button>
            </SaveBar>

            <Page
                fullWidth
                title={product?.title || 'Product Details'}
                secondaryActions={product?.status?.trim() ? <Badge>{productStatusLabel(product.status)}</Badge> : undefined}
                backAction={{ content: 'Products', onAction: () => navigate('/products') }}
            >
                {error && (
                    <div style={{ marginBottom: 12 }}>
                        <Banner title="Error" tone="critical">{error}</Banner>
                    </div>
                )}

                <CopyHistoryModal key={productId} productId={productId} open={copyHistoryOpen} onClose={() => setCopyHistoryOpen(false)}
                    jobs={copyJobs} variants={product?.variants} onRetry={onRetryCopyJob}
                    disabled={submitting || loading || copyJobRunning || hasUnsavedChanges} />

                <Modal
                    open={copyModalOpen}
                    onClose={onCloseCopyModal}
                    title={copySourceSelection ? 'Review fitment replacement' : 'Copy fitments from another product'}
                    primaryAction={copySourceSelection ? {
                        content: copyTargetFitmentCount > 0 ? 'Replace fitments' : 'Copy fitments',
                        destructive: copyTargetFitmentCount > 0,
                        onAction: onConfirmCopyFitments,
                        loading: submitting,
                        disabled: loading || copyJobRunning,
                    } : undefined}
                    secondaryActions={copySourceSelection ? [
                        {
                            content: 'Back',
                            onAction: () => setCopySourceSelection(null),
                            disabled: submitting || loading,
                        },
                        {
                            content: 'Cancel',
                            onAction: onCloseCopyModal,
                            disabled: submitting || loading,
                        },
                    ] : undefined}
                >
                    <Modal.Section>
                        {copySourceSelection ? (
                            <div style={{ display: 'grid', gap: 16 }}>
                                <Banner
                                    title={copyTargetFitmentCount > 0
                                        ? `Replace ${formatFitmentCountLabel(copyTargetFitmentCount)}?`
                                        : `Copy ${formatFitmentCountLabel(copySourceSelection.source.fitmentCount)}?`}
                                    tone={copyTargetFitmentCount > 0 ? 'warning' : 'info'}
                                >
                                    <Text as="p" variant="bodySm">
                                        {copyTargetFitmentCount > 0 ? (
                                            <>
                                                The current list for {copyTargetScopeLabel} will be removed and replaced with{' '}
                                                <strong>{formatFitmentCountLabel(copySourceSelection.source.fitmentCount)}</strong>{' '}
                                                from &quot;{copySourceSelection.product.title}&quot;
                                                {copySourceSelection.product.sku ? ` (SKU ${copySourceSelection.product.sku})` : ''}.
                                            </>
                                        ) : (
                                            <>
                                                The <strong>{formatFitmentCountLabel(copySourceSelection.source.fitmentCount)}</strong>{' '}
                                                from &quot;{copySourceSelection.product.title}&quot;
                                                {copySourceSelection.product.sku ? ` (SKU ${copySourceSelection.product.sku})` : ''}{' '}
                                                will become the complete list for {copyTargetScopeLabel}.
                                            </>
                                        )}
                                    </Text>
                                </Banner>

                                <Text as="p" variant="bodySm">
                                    {includeCopiedFitmentNotes
                                        ? 'Fitment notes from the source product will also be copied.'
                                        : 'Fitment notes from the source product will not be copied.'}
                                </Text>

                                <Text as="p" variant="bodySm" tone="subdued">
                                    {copyRunsInBackground
                                        ? 'This replacement starts immediately and continues in the background. It cannot be discarded from the Save bar.'
                                        : 'The replacement will be added to this page\u2019s draft. You can review it before clicking Save.'}
                                </Text>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: 16 }}>
                                <Banner
                                    title={copyTargetFitmentCount > 0
                                        ? 'Existing fitments will be replaced'
                                        : 'The source becomes the complete fitment list'}
                                    tone={copyTargetFitmentCount > 0 ? 'warning' : 'info'}
                                >
                                    <Text as="p" variant="bodySm">
                                        Copying fitments does not merge two lists.{' '}
                                        {copyTargetFitmentCount > 0 ? (
                                            <>
                                                All <strong>{formatFitmentCountLabel(copyTargetFitmentCount)}</strong> assigned to{' '}
                                                {copyTargetScopeLabel} will be removed and replaced with fitments from the selected product.
                                            </>
                                        ) : (
                                            <>
                                                Fitments from the selected product will become the complete list for {copyTargetScopeLabel}.
                                            </>
                                        )}
                                    </Text>
                                </Banner>

                                <div style={{ display: 'flex', gap: 12, alignItems: 'end' }}>
                                    <div style={{ flex: 1 }}>
                                        <TextField
                                            label="Search products"
                                            value={copyQuery}
                                            onChange={setCopyQuery}
                                            autoComplete="off"
                                            placeholder="Search by title, SKU, or vendor"
                                        />
                                    </div>
                                </div>

                                <Checkbox
                                    label="Include fitment notes"
                                    checked={includeCopiedFitmentNotes}
                                    onChange={setIncludeCopiedFitmentNotes}
                                    disabled={submitting || loading || copyJobRunning}
                                />
                                <Text as="p" variant="bodySm" tone="subdued">
                                    After confirmation, the replacement runs in the background. You can leave this page while it finishes.
                                </Text>

                                <div style={{ display: 'grid', gap: 8 }}>
                                    {/* Keep the copy picker lightweight so the modal reads like a compact product list. */}
                                    {copySourceProducts.map((candidate) => (
                                        <button
                                            key={candidate.id}
                                            type="button"
                                            onClick={() => onReviewCopySource(candidate)}
                                            disabled={submitting || loading || copyJobRunning}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '56px minmax(0, 1fr) auto auto',
                                                gap: 12,
                                                alignItems: 'center',
                                                padding: 12,
                                                border: '1px solid #d0d5dd',
                                                borderRadius: 8,
                                                background: '#fff',
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <ProductThumbnail source={candidate.featuredImage} alt={candidate.title} size="small" />
                                            <div style={{ minWidth: 0 }}>
                                                <Text as="p" variant="bodyMd" fontWeight="medium">{candidate.title}</Text>
                                                <Text as="p" variant="bodySm" tone="subdued">SKU: {candidate.sku || '-'}</Text>
                                            </div>
                                            <Text as="p" variant="bodySm" tone="subdued">{formatFitmentCountLabel(candidate.fitmentCount)}</Text>
                                            <Text as="p" variant="bodySm">Review</Text>
                                        </button>
                                    ))}

                                    {!copySearching && copyQuery.trim() && copySourceProducts.length === 0 && (
                                        <Text as="p" variant="bodyMd" tone="subdued">
                                            No matching products found.
                                        </Text>
                                    )}
                                </div>
                            </div>
                        )}
                    </Modal.Section>
                </Modal>

                <Modal open={!!scopeAction} onClose={() => !submitting && !saveAttempt && setScopeAction(null)}
                    title={scopeAction?.operation === 'clear' ? 'Remove all fitments' : scopeAction?.operation === 'universal' ? 'Mark product as universal' : 'Turn off universal status'}
                    primaryAction={{ content: saveAttempt ? 'Retry' : 'Confirm', destructive: scopeAction?.operation !== 'specific',
                        loading: submitting, onAction: () => scopeAction && submitChanges(saveAttempt ?? {
                            mutationId: crypto.randomUUID(), variantId: product?.selectedVariantId, changes: [],
                            operation: scopeAction.operation, scopeVersion: scopeAction.version,
                        }) }}
                    secondaryActions={[{ content: 'Cancel', disabled: submitting || !!saveAttempt, onAction: () => setScopeAction(null) }]}>
                    <Modal.Section><Text as="p">{scopeAction?.operation === 'specific'
                        ? 'This product will require individual fitment assignments. Previously removed assignments will not be restored.'
                        : `This will remove all ${scopeAction?.count.toLocaleString()} assignments in the selected product or variant. ${scopeAction?.operation === 'universal' ? 'The product will match all vehicles.' : ''} This action saves immediately.`}</Text></Modal.Section>
                </Modal>
                <Modal open={blocker.state === 'blocked'} onClose={() => blocker.state === 'blocked' && blocker.reset()}
                    title="Unsaved fitment changes"
                    primaryAction={{ content: 'Keep editing', onAction: () => blocker.state === 'blocked' && blocker.reset() }}
                    secondaryActions={[{ content: 'Discard and leave', destructive: true, disabled: !!saveAttempt || !!syncRecoveryId,
                        onAction: () => { if (blocker.state === 'blocked') { receiveDraft(new FitmentDraft()); blocker.proceed(); } } }]}>
                    <Modal.Section><Text as="p">Save or discard your changes before leaving this product.</Text></Modal.Section>
                </Modal>
                <Modal open={reviewChanges} onClose={() => setReviewChanges(false)} title="Unsaved fitment changes"
                    primaryAction={{ content: 'Done', onAction: () => setReviewChanges(false) }}>
                    <Modal.Section>
                        {pendingFitmentRows.length === 0 ? <Text as="p" tone="subdued">No unsaved changes.</Text> : (
                            <div className="pf-fitment-review">
                                <Text as="p" tone="subdued">{pendingFitmentSummary}</Text>
                                {pendingFitmentRows.map((row) => {
                                    const keys = row.fitments.map(fitmentKey);
                                    const changes = keys.map((key) => editDraft.changes.get(key)!);
                                    const title = productDetailsFitmentFields.map((field) => row.fieldValues[normalizeFitmentKey(field)]).filter(Boolean).join(' · ');
                                    const actions = [
                                        { label: 'Add fitment', changes: changes.filter((change) => !change.before) },
                                        { label: 'Remove fitment', changes: changes.filter((change) => !change.after) },
                                        { label: 'Update notes', changes: changes.filter((change) => change.before && change.after) },
                                    ].filter((action) => action.changes.length > 0);
                                    const notes = changes.find((change) => change.after)?.after?.notes ?? [];
                                    return (
                                        <div key={row.key} className="pf-fitment-review-row" role="group" aria-label={title}>
                                            <div className="pf-fitment-review-details">
                                                <Text as="p" fontWeight="medium">{title}</Text>
                                                {actions.map((action) => (
                                                    <Text as="p" tone="subdued" key={action.label}>
                                                        {action.label} · {formatFitmentCountLabel(action.changes.length)}
                                                        {actions.length > 1 && standardYearFitmentField && ` (${formatYearRange(action.changes.map((change) => getFitmentDisplayValue((change.after ?? change.before)!, standardYearFitmentField)))})`}
                                                    </Text>
                                                ))}
                                                {notes.map((note, index) => <Text as="p" key={index}>{note.text}</Text>)}
                                                {notes.length === 0 && actions.some((action) => action.label === 'Update notes') && <Text as="p">All notes removed.</Text>}
                                            </div>
                                            <Button disabled={editingBlocked} accessibilityLabel={`Undo changes to ${title}`} onClick={() => receiveDraft(editDraftRef.current.undo(keys))}>Undo</Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Modal.Section>
                </Modal>

                <Modal
                    open={!!editingFitment}
                    onClose={() => {
                        setEditingFitment(null);
                        setFitmentEditError('');
                    }}
                    title="Edit fitment"
                    footer={<Text as="p" variant="bodySm" tone="subdued">Apply here, then save on the product page.</Text>}
                    onTransitionEnd={() => {
                        if (editingFitment?.focusNotes) fitmentNotesRef.current?.querySelector('textarea')?.focus();
                    }}
                    primaryAction={{
                        content: 'Apply changes',
                        onAction: onApplyFitmentEdit,
                        disabled: editingBlocked || !editingFitment,
                    }}
                    secondaryActions={[{
                        content: 'Cancel',
                        onAction: () => {
                            setEditingFitment(null);
                            setFitmentEditError('');
                        },
                        disabled: submitting || loading,
                    }]}
                >
                    <Modal.Section>
                        {editingFitment && (
                            <div className="pf-fitment-edit">
                                {fitmentEditError && (
                                    <Banner title="Cannot apply fitment changes" tone="critical">
                                        {fitmentEditError}
                                    </Banner>
                                )}

                                {editableFitmentFields.length > 0 && (
                                    <section aria-label="Vehicle details">
                                    <div className="pf-fitments-section-heading"><Text as="h3" variant="headingSm">Vehicle details</Text></div>
                                    <div className="pf-fitment-edit-fields">
                                        {editableFitmentFields.map((field) => {
                                            const key = normalizeFitmentKey(field);
                                            return (
                                                <FitmentValueAutocomplete
                                                    key={key}
                                                    label={field.label}
                                                    value={getFitmentFieldValue(editingFitment.fieldValues, field)}
                                                    options={editFieldOptions[key] ?? []}
                                                    disabled={editingBlocked || isFitmentFieldDisabled(editableFitmentFields, editingFitment.fieldValues, field, false)}
                                                    loading={loadingEditFieldOptions[key]}
                                                    placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                                    onChange={(value, immediate) => onUpdateEditedFitmentField(field, value, immediate)}
                                                />
                                            );
                                        })}
                                    </div>
                                    </section>
                                )}

                                {standardYearFitmentField && <section className="pf-fitment-edit-section" aria-label="Years">
                                <div className="pf-fitments-section-heading">
                                    <Text as="h3" variant="headingSm">Years</Text>
                                    <Button variant="plain" onClick={onAddYearGroupRange} disabled={editingBlocked}>+ Add range</Button>
                                </div>
                                <div style={{ display: 'grid', gap: 12 }}>
                                    {editingFitment.ranges.map((range) => (
                                        <div
                                            key={range.id}
                                            className={`pf-fitment-range${editingFitment.ranges.length === 1 ? ' pf-fitment-range--single' : ''}`}
                                        >
                                            <TextField
                                                label="Start year"
                                                value={range.start}
                                                disabled={editingBlocked}
                                                onChange={(value) => onUpdateYearGroupRange(range.id, 'start', value)}
                                                onSpinnerChange={(value) => onUpdateYearGroupRange(range.id, 'start', value, true)}
                                                autoComplete="off"
                                                type="integer"
                                                min={1900}
                                                max={2099}
                                            />
                                            <TextField
                                                label="End year"
                                                value={range.end}
                                                disabled={editingBlocked}
                                                onChange={(value) => onUpdateYearGroupRange(range.id, 'end', value)}
                                                onSpinnerChange={(value) => onUpdateYearGroupRange(range.id, 'end', value, true)}
                                                autoComplete="off"
                                                type="integer"
                                                min={1900}
                                                max={2099}
                                            />
                                            {editingFitment.ranges.length > 1 && <Button
                                                variant="plain"
                                                tone="critical"
                                                onClick={() => onRemoveYearGroupRange(range.id)}
                                                disabled={editingBlocked}
                                                accessibilityLabel={`Remove year range ${editingFitment.ranges.indexOf(range) + 1}`}
                                            >
                                                Remove
                                            </Button>}
                                        </div>
                                    ))}
                                </div>

                                </section>}
                                <section className="pf-fitment-edit-section" aria-label="Notes" ref={fitmentNotesRef}>
                                    <FitmentNotesEditor
                                        label="Notes"
                                        noteLines={editingFitment.noteLines}
                                        onChange={(noteLines) => setEditingFitment((current) => current ? { ...current, noteLines } : current)}
                                        disabled={editingBlocked}
                                        autoFocus={editingFitment.focusNotes}
                                    />
                                    {standardYearFitmentField && <div style={{ marginTop: 8 }}><Text as="p" variant="bodySm" tone="subdued">Notes apply to all years selected above.</Text></div>}
                                </section>
                            </div>
                        )}
                    </Modal.Section>
                </Modal>

                <VariantReviewDialog review={variantReview?.review ?? null} currentVariantId={selectedVariantId}
                    busy={submitting} retry={!!saveAttempt} error={error}
                    onClose={() => setVariantReview(null)}
                    onExclude={ids => void excludeConflictingVariants(ids)}
                    onSave={() => variantReview && void submitChanges(saveAttempt ?? variantReview.attempt)} />

                <ProductSummaryCard product={product} isUniversal={draftIsUniversal} adminUrl={shopifyAdminProductUrl}>
                    {showFitmentScopeSelector && (
                        <div className="pf-variant-scope-row">
                            <div className="pf-variant-scope-select">
                                <Select
                                    label="Fitments for"
                                    labelInline
                                    options={fitmentScopeOptions}
                                    value={selectedScopeValue}
                                    disabled={loading || submitting || hasUnsavedChanges || copyJobRunning}
                                    onChange={(value) => {
                                        setError('');
                                        setAssignedFitmentSearch('');
                                        setDebouncedFitmentSearch('');
                                        setEditingFitment(null);
                                        setFitmentEditError('');
                                        void loadDetails(value);
                                    }}
                                />
                            </div>
                            {isVariantScope && (product?.variants.length ?? 0) > 1 && <div className="pf-variant-apply">
                                <Checkbox label={targetVariantIds.length && targetVariantIds.length < (product?.variants.length ?? 0) - 1
                                    ? `Apply to ${targetVariantIds.length + 1} variants` : 'Apply to all variants'}
                                    checked={targetVariantIds.length > 0} disabled={editingBlocked}
                                    onChange={checked => setTargetVariantIds(checked ? (product?.variants ?? []).filter(v => v.id !== selectedVariantId).map(v => v.id) : [])} />
                                <VariantTargetPicker open={variantPickerOpen} variants={product?.variants ?? []}
                                    currentVariantId={selectedVariantId} selected={targetVariantIds} disabled={editingBlocked}
                                    onToggle={() => setVariantPickerOpen(open => !open)} onClose={() => setVariantPickerOpen(false)}
                                    onSelect={ids => { setTargetVariantIds(ids); setVariantReview(null); }} />
                                <Tooltip content="For this save only: add missing fitments and edit or remove matching fitments. Existing differences are preserved. Copy and Remove all affect only the current variant.">
                                    <button type="button" className="pf-fitments-help" aria-label="How variant edits apply">?</button>
                                </Tooltip>
                                {targetVariantIds.length > 100 && <Text as="p" tone="critical">Choose up to 100 other variants.</Text>}
                            </div>}
                        </div>
                    )}
                </ProductSummaryCard>

                <div className="pf-product-fitments" style={{ marginTop: 16 }}>
                    <Card>
                        <div className="pf-fitments-header">
                            <div className="pf-fitments-title">
                                <Text as="h3" variant="headingMd">Fitments</Text>
                            </div>
                            {!isVariantScope && (
                                <div className="pf-fitments-universal">
                                    <Checkbox label="Universal product" checked={draftIsUniversal} onChange={onToggleUniversal} disabled={editingBlocked || hasUnsavedChanges} />
                                    <Tooltip content="Universal products match all vehicles and do not use individual fitment mappings.">
                                        <span className="pf-fitments-help" aria-label="About universal products">?</span>
                                    </Tooltip>
                                </div>
                            )}
                            <Popover
                                active={fitmentActionsOpen}
                                activator={
                                    <Button
                                        disclosure
                                        onClick={() => setFitmentActionsOpen((current) => !current)}
                                        disabled={submitting || loading}
                                    >
                                        More actions
                                    </Button>
                                }
                                onClose={() => setFitmentActionsOpen(false)}
                            >
                                <ActionList
                                    items={[
                                        {
                                            content: 'Copy fitments from product',
                                            disabled: copyJobRunning || hasUnsavedChanges,
                                            onAction: () => {
                                                setFitmentActionsOpen(false);
                                                onOpenCopyModal();
                                            },
                                        },
                                        {
                                            content: 'View copy history',
                                            onAction: () => {
                                                setFitmentActionsOpen(false);
                                                setCopyHistoryOpen(true);
                                            },
                                        },
                                        {
                                            content: showRawTags ? 'Hide tags' : 'Show tags',
                                            disabled: draftIsUniversal || editingBlocked,
                                            onAction: () => {
                                                setShowRawTags((current) => !current);
                                                setFitmentActionsOpen(false);
                                            },
                                        },
                                        {
                                            content: 'Remove all fitments',
                                            destructive: true,
                                            disabled: (product?.fitmentCount ?? 0) === 0 || editingBlocked || hasUnsavedChanges,
                                            onAction: () => {
                                                setFitmentActionsOpen(false);
                                                void prepareScopeAction('clear');
                                            },
                                        },
                                    ]}
                                />
                            </Popover>
                        </div>

                        {hasUnsavedChanges && <div className="pf-fitments-pending" role="status">
                            <Text as="p">{syncRecoveryId ? 'Fitment changes saved; Shopify synchronization pending.' : pendingFitmentSummary}{editDraft.changes.size >= 400 ? ` · Save or discard before adding more changes (limit ${FITMENT_CHANGE_LIMIT}).` : ''}</Text>
                            {editDraft.changes.size > 0 && <Button variant="plain" onClick={() => setReviewChanges(true)}>Review changes</Button>}
                            {(saveAttempt || syncRecoveryId) && <Text as="p">Retry Save to confirm the previous attempt and finish Shopify synchronization.</Text>}
                        </div>}

                        {activeCopyJob && (
                            <div style={{ marginTop: 16 }}>
                                <Banner title="Copying fitments" tone="info">
                                    <div style={{ display: 'grid', gap: 10 }}>
                                        <Text as="p" variant="bodyMd">
                                            From {activeCopyJob.sourceProductTitle}
                                        </Text>
                                        <ProgressBar progress={activeCopyJob.progressPercent} size="small" />
                                        <Text as="p" variant="bodySm">
                                            {activeCopyJob.message}
                                        </Text>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            You can leave this page. The copy will continue in the background.
                                        </Text>
                                    </div>
                                </Banner>
                            </div>
                        )}

                        {latestFailedCopyJob && (
                            <div style={{ marginTop: 16 }}>
                                <Banner title="Fitment copy failed" tone="critical">
                                    <div style={{ display: 'grid', gap: 10 }}>
                                        <Text as="p" variant="bodySm">{latestFailedCopyJob.message}</Text>
                                        <div>
                                            <Button
                                                variant="plain"
                                                onClick={() => onRetryCopyJob(latestFailedCopyJob)}
                                                disabled={submitting || loading || copyJobRunning || hasUnsavedChanges}
                                            >
                                                Retry copy
                                            </Button>
                                        </div>
                                    </div>
                                </Banner>
                            </div>
                        )}

                        {draftIsUniversal ? (
                            <div className="pf-fitments-universal-message">
                                <Text as="p" variant="bodyMd" tone="subdued">
                                    Fits all vehicles. Specific fitments aren’t needed.
                                </Text>
                            </div>
                        ) : (
                            <>
                                {fitmentSelectorConfigured && (
                                    <div className="pf-fitment-composer">
                                        <div className="pf-fitment-selector pf-fitment-selector--flush">
                                            {productDetailsFitmentFields.map((field) => {
                                                const key = normalizeFitmentKey(field);
                                                const isYearField = field === standardYearFitmentField;
                                                const fieldDisabled = editingBlocked || isFitmentFieldDisabled(productDetailsFitmentFields, selectedFitmentValues, field, false);
                                                return (
                                                    <React.Fragment key={key}>
                                                        <div className="pf-fitment-selector__field">
                                                            <FitmentValueAutocomplete
                                                                label={field.label}
                                                                options={fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'select'), value: '' }]}
                                                                value={getFitmentFieldValue(selectedFitmentValues, field)}
                                                                disabled={fieldDisabled}
                                                                loading={loadingFieldOptions[key]}
                                                                placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                                                onChange={(value, immediate) => {
                                                                    setOptionsDelay(immediate ? 0 : 200);
                                                                    setSelectedFitmentValues((current) => updateFitmentFieldValue(productDetailsFitmentFields, current, field, value));
                                                                }}
                                                            />
                                                        </div>
                                                        {isYearField && (
                                                            <div className="pf-fitment-selector__field">
                                                                <FitmentValueAutocomplete
                                                                    label="Year to"
                                                                    options={fieldOptions[key] ?? []}
                                                                    value={selectedFitmentYearTo}
                                                                    disabled={fieldDisabled || !selectedFitmentYearValue}
                                                                    loading={loadingFieldOptions[key]}
                                                                    placeholder="Select year to"
                                                                    error={addFitmentYearRange.error}
                                                                    onChange={setSelectedFitmentYearTo}
                                                                />
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                            <div className="pf-fitment-selector__action">
                                                <Button variant="plain" onClick={() => {
                                                    setShowNewFitmentNotes((current) => !current);
                                                    if (!showNewFitmentNotes) setNewFitmentNoteLines((current) => current.length > 0 ? current : ['']);
                                                }} disabled={editingBlocked} ariaExpanded={showNewFitmentNotes}>
                                                    {showNewFitmentNotes ? 'Hide notes' : '+ Add notes'}
                                                </Button>
                                                <Button variant="primary" onClick={onAddFitment} disabled={!canAddFitment || editingBlocked}>Add fitment</Button>
                                            </div>
                                        </div>
                                        {addFitmentYearRange.error && (
                                            <div style={{ marginTop: 8 }}>
                                                <Text as="p" variant="bodySm" tone="critical">
                                                    {addFitmentYearRange.error}
                                                </Text>
                                            </div>
                                        )}
                                        {showNewFitmentNotes && <div style={{ marginTop: 16 }}>
                                                <FitmentNotesEditor
                                                    label="Notes"
                                                    noteLines={newFitmentNoteLines}
                                                    onChange={setNewFitmentNoteLines}
                                                    disabled={editingBlocked}
                                                    autoFocus
                                                />
                                        </div>}
                                    </div>
                                )}

                                <div className="pf-assigned-fitments">
                                    {showAssignedFitmentToolbar && <div className="pf-assigned-fitments-toolbar">
                                        <Text as="h4" variant="headingSm">Assigned fitments</Text>
                                        <div className="pf-assigned-fitments-controls">
                                                <div className="pf-assigned-fitments-search">
                                                    <TextField
                                                        label="Search assigned fitments"
                                                        labelHidden
                                                        value={assignedFitmentSearch}
                                                        onChange={setAssignedFitmentSearch}
                                                        autoComplete="off"
                                                        placeholder="Search fitments or notes"
                                                        clearButton
                                                        onClearButtonClick={() => setAssignedFitmentSearch('')}
                                                    />
                                                </div>
                                            {standardYearFitmentField && (
                                                <div
                                                    aria-label="Assigned fitment view"
                                                    role="group"
                                                    style={{
                                                        display: 'inline-flex',
                                                        gap: 2,
                                                        padding: 3,
                                                        border: '1px solid #d0d5dd',
                                                        borderRadius: 14,
                                                        background: '#f7f7f7',
                                                    }}
                                                >
                                                    {([
                                                        { value: 'grouped' as AssignedFitmentView, label: 'Year ranges' },
                                                        { value: 'individual' as AssignedFitmentView, label: 'Individual years' },
                                                    ]).map((option) => {
                                                        const isSelected = effectiveAssignedFitmentView === option.value;
                                                        return (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                aria-pressed={isSelected}
                                                                onClick={() => {
                                                                    setAssignedFitmentView(option.value);
                                                                }}
                                                                style={{
                                                                    minHeight: 34,
                                                                    padding: '6px 18px',
                                                                    border: '1px solid transparent',
                                                                    borderRadius: 11,
                                                                    background: isSelected ? '#fff' : 'transparent',
                                                                    boxShadow: isSelected ? '0 1px 3px rgba(0, 0, 0, 0.18)' : 'none',
                                                                    color: '#202223',
                                                                    cursor: 'pointer',
                                                                    font: 'inherit',
                                                                    fontWeight: isSelected ? 600 : 400,
                                                                    lineHeight: '20px',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>}
                                    <div style={{ marginTop: 10 }}>
                                        {assignedFitmentPageError && <Banner tone="critical" title="Unable to load fitments"
                                            action={{ content: 'Retry loading fitments', onAction: () => { void loadAssignedFitmentPage(requestedFitmentPageRef.current); } }}>
                                            {assignedFitmentPageError}
                                        </Banner>}
                                        {assignedFitmentPageLoading && assignedFitmentRows.length > 0 && <div role="status" style={{ paddingBottom: 8 }}><Spinner size="small" accessibilityLabel="Loading assigned fitments" /></div>}
                                        {assignedFitmentRows.length > 0 ? (
                                            <>
                                            <IndexTable
                                                resourceName={{ singular: 'fitment', plural: 'fitments' }}
                                                itemCount={assignedFitmentRows.length}
                                                headings={[
                                                    ...productDetailsFitmentFields.map((field) => ({ title: field.label })),
                                                    { title: 'Notes' },
                                                    ...(showRawTags ? [{ title: 'Tag' }] : []),
                                                    { title: '' },
                                                ] as any}
                                                selectable={false}
                                            >
                                                {assignedFitmentRows.map((row, index) => {
                                                    const rowFitmentKeys = row.fitments.map(getFitmentIdentity);
                                                    const notesFitment = row.fitments[0];
                                                    const noteLines = getFitmentNoteLines(notesFitment);
                                                    const notesText = getFitmentNotesText(notesFitment);
                                                    return (
                                                        <IndexTable.Row id={row.key} key={row.key} position={index}
                                                            tone={row.fitments.some(fitment => editDraft.changes.has(fitmentKey(fitment))) ? 'subdued' : undefined}>
                                                            {productDetailsFitmentFields.map((field) => (
                                                                <IndexTable.Cell key={`${row.key}-${field.fieldIndex}`}>
                                                                    {row.fieldValues[normalizeFitmentKey(field)]}
                                                                </IndexTable.Cell>
                                                            ))}
                                                            <IndexTable.Cell>
                                                                <div className="pf-fitment-note-preview">
                                                                    {notesText ? (
                                                                        <button
                                                                            type="button"
                                                                            className="pf-fitment-note-link"
                                                                            onClick={() => onOpenFitmentEditor(row, true)}
                                                                            disabled={editingBlocked}
                                                                            title={notesText}
                                                                        >
                                                                            <span>{noteLines[0]}</span>
                                                                            {noteLines.length > 1 && <span className="pf-fitment-note-count">+{noteLines.length - 1} more</span>}
                                                                        </button>
                                                                    ) : (
                                                                        <Button variant="plain" onClick={() => onOpenFitmentEditor(row, true)} disabled={editingBlocked}>+ Add notes</Button>
                                                                    )}
                                                                </div>
                                                            </IndexTable.Cell>
                                                            {showRawTags && (
                                                                <IndexTable.Cell>
                                                                    {row.fitments.length === 1 ? row.fitments[0].tag || '-' : `${row.fitments.length} tags`}
                                                                </IndexTable.Cell>
                                                            )}
                                                            <IndexTable.Cell>
                                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                                                                    {visibleFitmentFields.length > 0 && (
                                                                        <Button
                                                                            variant="tertiary"
                                                                            onClick={() => onOpenFitmentEditor(row)}
                                                                            disabled={editingBlocked}
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                    )}
                                                                    <Button icon={DeleteIcon} variant="tertiary" tone="critical" onClick={() => onRemoveFitments(rowFitmentKeys, row.key)} disabled={editingBlocked}>
                                                                        Remove
                                                                    </Button>
                                                                </div>
                                                            </IndexTable.Cell>
                                                        </IndexTable.Row>
                                                    );
                                                })}
                                            </IndexTable>
                                            </>
                                        ) : isAssignedFitmentsLoading ? (
                                            <div className="pf-fitments-empty" role="status">
                                                <Spinner accessibilityLabel="Loading assigned fitments" size="small" />
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    Loading assigned fitments...
                                                </Text>
                                            </div>
                                        ) : assignedFitmentPageError ? null : (
                                            <div className="pf-fitments-empty">
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {hasAssignedFitmentRefinements
                                                        ? 'No fitments match the current filters.'
                                                        : (product?.fitmentCount ?? 0) > 0 ? 'No fitments to display on this page. Review your changes or choose another page.' : 'No fitments yet. Add a vehicle above.'}
                                                </Text>
                                            </div>
                                        )}
                                            {assignedFitmentTotalPages > 1 && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                                                    <Text as="p" variant="bodySm" tone="subdued">
                                                        Page {assignedFitmentCurrentPage} of {assignedFitmentTotalPages}
                                                    </Text>
                                                    <Pagination
                                                        hasPrevious={!assignedFitmentPageLoading && !!product?.fitmentHasPrevious}
                                                        hasNext={!assignedFitmentPageLoading && !!product?.fitmentHasNext}
                                                        onPrevious={() => loadAssignedFitmentPage(Math.max(1, assignedFitmentCurrentPage - 1))}
                                                        onNext={() => loadAssignedFitmentPage(assignedFitmentCurrentPage + 1)}
                                                    />
                                                </div>
                                            )}
                                    </div>
                                </div>

                                {!isVariantScope && importableDetectedFitments.length > 0 && (
                                    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e3e3e3' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                                            <div>
                                                <Text as="h4" variant="headingSm">Detected fitments</Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Potential vehicle tags detected from the product&apos;s current Shopify tags.
                                                </Text>
                                            </div>
                                            {/* Import converts detected tags into the shop's current canonical tag format, but still only stages them locally until Save. */}
                                            <Button onClick={onImportDetectedFitments} disabled={editingBlocked}>
                                                Import detected fitments
                                            </Button>
                                        </div>
                                        <IndexTable
                                            resourceName={{ singular: 'detected fitment', plural: 'detected fitments' }}
                                            itemCount={importableDetectedFitments.length}
                                            headings={[
                                                ...productDetailsFitmentFields.map((field) => ({ title: field.label })),
                                                { title: 'Detected tag' },
                                                { title: 'Will import as' },
                                            ] as any}
                                            selectable={false}
                                        >
                                            {importableDetectedFitments.map((fitment, index) => (
                                                <IndexTable.Row id={fitment.canonicalTag} key={fitment.canonicalTag} position={index}>
                                                    {productDetailsFitmentFields.map((field) => (
                                                        <IndexTable.Cell key={`${fitment.canonicalTag}-${field.fieldIndex}`}>
                                                            {getFitmentDisplayValue(fitment, field)}
                                                        </IndexTable.Cell>
                                                    ))}
                                                    <IndexTable.Cell>{fitment.sourceTag}</IndexTable.Cell>
                                                    <IndexTable.Cell>{fitment.canonicalTag}</IndexTable.Cell>
                                                </IndexTable.Row>
                                            ))}
                                        </IndexTable>
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                    <RecentCopyActivity key={productId} jobs={copyJobs} variants={product?.variants} onRetry={onRetryCopyJob}
                        disabled={submitting || loading || copyJobRunning || hasUnsavedChanges}
                        onViewAll={() => setCopyHistoryOpen(true)} />
                </div>
            </Page>
        </>
    );
};

export default observer(ProductDetailsPage);
